import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { additionalDocTypeOptions } from './options';
import { AddDocBox } from './AddDocBox';
import { PAGE_TEXT } from '../lang';
import {
  type AdditionalDocFormData,
  addApplicationItem,
  useUpdateAdditionalDocument,
} from '../../../graphql';
import { Alert, type AlertDetails } from '../../../../../components';
import { getOptionsLabel } from '../../../../Monitor/components';
import { handleFileRename } from '../../../../../utils/handleFileRename';
import { type FormController } from '../../../../../utils';

type AddDocContentProps = {
  applicationNo: string;
  additionalDocuments?: AdditionalDocFormData;
  isConditionEditable?: boolean;
  enableEdit: FormController;
  addDocIndex: number;
  enableDelete: boolean;
  handleDelete: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
  refetchApplication: () => void;
};

export const AddDocContent: React.FC<AddDocContentProps> = (
  props: AddDocContentProps,
) => {
  const { applicationId } = useParams();
  const {
    applicationNo = '',
    additionalDocuments,
    isConditionEditable,
    enableEdit,
    addDocIndex,
    refetchApplication,
    handleDelete,
    handleLoading,
    enableDelete,
  } = props;

  const additionalDocId = additionalDocuments?._id ?? '';

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [isCreateMode, setIsCreateMode] = useState<boolean>(!additionalDocId);

  const InitialAdditionalDocData: AdditionalDocFormData =
    additionalDocuments ?? {
      documentType: '',
      supportingDocuments: [],
    };

  const createAdditionalDocument = addApplicationItem(
    'additionalDocuments',
    refetchApplication,
  );
  const updateAdditionalDocument = useUpdateAdditionalDocument();
  const handleSubmit = async (values: AdditionalDocFormData): Promise<void> => {
    const { supportingDocuments, documentType } = values;
    if (!supportingDocuments.length) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: 'Please upload relevant supporting documents.',
        alertType: 'warning',
      });
      return;
    }
    await handleLoading(async () => {
      try {
        const inputValue = {
          documentType,
          supportingDocuments: supportingDocuments.map((doc, index) => {
            const { name, ...other } = doc;
            return {
              name: handleFileRename(
                applicationNo,
                getOptionsLabel(additionalDocTypeOptions, documentType)
                  .replaceAll(' ', '_')
                  .toLocaleLowerCase(),
                index + 1,
              ),
              ...other,
            };
          }),
        };

        if (isCreateMode) {
          await createAdditionalDocument({
            variables: {
              id: applicationId,
              input: {
                additionalDocument: {
                  ...inputValue,
                },
              },
            },
          });
          setIsCreateMode(false);
        } else {
          await updateAdditionalDocument({
            variables: {
              id: additionalDocId,
              ...inputValue,
            },
          });
        }
        refetchApplication();
        alertDetailDef = {
          alertContent: isConditionEditable
            ? PAGE_TEXT.CONDITION_SAVE_ALTER_TEXT
            : 'Saved Additional Document Successfully.',
          alertType: 'success',
        };
      } catch (e) {
        alertDetailDef = {
          alertContent: 'Error Occurs.' + (e as Error)?.message,
          alertType: 'error',
        };
      } finally {
        setOpenAlert(true);
        setAlertDetails(alertDetailDef);
      }
    });
  };

  const addDocBoxData = {
    InitialAdditionalDocData,
    boxText: {
      subtitle: PAGE_TEXT.ADDITIONAL_DOCUMENTS.TITLE,
      caption: PAGE_TEXT.ADDITIONAL_DOCUMENTS.CAPTION,
      filesPanel: {
        title: PAGE_TEXT.ADDITIONAL_DOCUMENTS.FILES_PANEL.PANEL_TITLE,
        caption: PAGE_TEXT.ADDITIONAL_DOCUMENTS.FILES_PANEL.PANEL_CAPTION,
      },
    },
    enableEdit,
    addDocIndex,
    docOptions: additionalDocTypeOptions,
    handleSubmit,
    enableDelete,
    handleDelete,
    handleLoading,
  };

  return (
    <div>
      <AddDocBox {...addDocBoxData} />
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </div>
  );
};
